<template>
  <div v-if="i18nReady" class="insurance-plan-filter" :class="classModifiers">
    <sp-quantum-select
      ref="picker"
      :key="searchResetKey"
      v-preserve-properties:items="models"
      v-preserve-properties:load-children="loadChildren"
      v-preserve-properties:selected="[selectedModel?.value]"
      v-preserve-properties:initial-path="initialPath"
      :aria-label="label"
      :disabled="disabled"
      :clearable="!isNonClearable"
      :required="isRequired"
      no-filter
      highlight-first-result
      :field-commits-highlight="fieldCommitsHighlight"
      focus-search-on-open
      item-title="text"
      mobile-expand-height-max
      :no-data-text="t('no_results')"
      :close-on-select="closeOnSelect"
      :hide-search-bar="hideSearchBar"
      :hide-title-bar="hideTitleBar"
      :search="searchQuery"
      :loading="loading"
      :location="location"
      :open="open"
      :permanent="permanent"
      :pointer="!hidePointer"
      :readonly="readonly"
      :title="stepTitle"
      :max-height="maxHeight"
      :start-on-root="startOnRoot"
      :input-test-id="TestId.finder.insuranceFilterInput"
      :item-test-id="TestId.finder.insuranceFilterOption"
      @update-search="handleUpdateSearch"
      @search-enter="handleSearchEnter"
      @update-selected="handleUpdateSelected"
      @update-open="handleUpdateOpen"
      @update-current-item="handleCurrentItemChange"
    >
      <div slot="activator">
        <slot
          name="activator"
          :label="label"
          :text="selectedModelText"
          :prepend-icon="prependIcon"
          :append-icon="appendIcon"
          :open="open"
        >
          <DefaultFilterActivator
            :value="activatorTitle"
            :label="label"
            :prepend-icon="prependIcon"
            :append-icon="appendIcon"
            :non-clearable="isNonClearable"
            :disabled="disabled"
            @clear="clearSelectedModel"
          />
        </slot>
      </div>
      <!-- The drill-down depth this bar visualises is already announced by the select's own step
           title, so it carries no information of its own to expose. -->
      <sp-progress
        v-if="!hideProgressBar"
        slot="before-content"
        v-preserve-properties:value="progress"
        class="insurance-plan-filter__progress"
        type="linear"
        size="micro"
        color="success"
        aria-hidden="true"
      ></sp-progress>
    </sp-quantum-select>
  </div>
</template>

<script setup>
import { vPreserveProperties } from "#ce/utils/property-bindings";
import { i18nReady, t } from "#ce/shared/filters/i18n";
import { computed, onMounted, ref, useTemplateRef, watch } from "vue";
import { isComposing } from "#ce/utils/events";
import { toBoolean, trimToUndefined } from "#ce/utils/props";
import { props as insurancePlanFilterProps } from "#ce/shared/filters/insurance-plan/components/props";
import { TestId } from "#/test-ids";
import { useForm } from "#ce/composables/useForm";
import { useInsuranceFilterStore } from "#ce/shared/filters/insurance-plan/store/insurance-filter";
import DefaultFilterActivator from "#ce/shared/filters/components/DefaultFilterActivator.ce.vue";

const emit = defineEmits(["update:model-value", "update:open"]);

const props = defineProps(insurancePlanFilterProps);

defineExpose({ props });

const hideProgressBar = computed(() => toBoolean(props.hideProgressBar));
const hideSearchBar = computed(() => toBoolean(props.hideSearchBar));
const isNonClearable = computed(() => toBoolean(props.nonClearable));
const isRequired = computed(() => toBoolean(props.required) || isNonClearable.value);
// A readonly picker selects nothing, so Enter in its search bar has no highlighted plan to commit.
const fieldCommitsHighlight = computed(() => !toBoolean(props.readonly));

const { mirrorFormValue } = useForm();

const store = useInsuranceFilterStore(props);
const { stepTitle, models, selectedModel, progress, loading, searchQuery, initialPath } = store;

// Bumped to remount `<sp-quantum-select :disabled="disabled">` and discard its drilled-in frame
// stack when search starts while the container is below root. At root there is
// no stack to discard, so the remount is skipped there to keep the focused
// search input alive (see `handleUpdateSearch`). `startOnRoot` is handled by the
// container itself via the `:start-on-root` prop.
const searchResetKey = ref(0);

// Mirror of the pre-update search-active state. `store.hasSearchQuery` reflects
// the post-update value once `searchQuery` is written, so it can't be used to
// detect the transition inside `handleUpdateSearch`.
const searchWasActive = ref(false);

async function loadReadonly() {
  await store.load();
}

// Once the plan list has loaded, a `modelValue` that doesn't resolve to a known plan (a stale or
// hand-typed `insurance_plan` slug) is invalid and must not look selected. Clearing it lets the parent
// drop it from the chip, sessionStorage, and the URL. The resolved `model` is already undefined in this
// case, so its watcher never fires — hence the explicit emit.
function clearInvalidModelValue() {
  // Only clear after a *successful* load. While loading or after a failed plan-list fetch we can't
  // validate, so never clear — otherwise a transient API failure would silently wipe a real selection
  // (the modal's plan validation fails closed the same way: it never clears on a load it couldn't
  // complete). No value: nothing to clear. Resolves to a known plan: valid.
  if (
    loading.value ||
    store.error.value ||
    trimToUndefined(props.modelValue) === undefined ||
    store.findByValue(props.modelValue)
  ) {
    return;
  }

  emit("update:model-value", undefined);
}

async function loadAndApplySelection() {
  await store.load(props.modelValue);

  if (!toBoolean(props.startOnRoot)) {
    selectedModel.value = store.findByValue(props.modelValue);
  }
  clearInvalidModelValue();
}

if (toBoolean(props.readonly)) {
  onMounted(loadReadonly);
} else {
  onMounted(loadAndApplySelection);
}

const model = computed(() => selectedModel.value?.value);

function emitModelValue(value) {
  if (value !== props.modelValue) {
    emit("update:model-value", value);
  }
}

watch(model, emitModelValue);

// The host app dispatches the one change per user update.
mirrorFormValue(model);

function syncSelectedFromModelValue(value) {
  const currentValue = selectedModel.value?.value;
  const found = store.findByValue(value);
  const newValue = found?.value;
  if (currentValue !== newValue) {
    selectedModel.value = found;
  }
  clearInvalidModelValue();
}

watch(() => props.modelValue, syncSelectedFromModelValue);

const selectedModelText = computed(() => selectedModel.value?.text);
const activatorTitle = computed(() => selectedModelText.value);

const open = ref(props.open);

function handleUpdateSelected({ detail }) {
  if (toBoolean(props.disabled)) {
    return;
  }
  const [selected] = detail;
  const [value] = selected;
  selectedModel.value = store.findByValue(value);
}

function clearSelectedModel() {
  if (toBoolean(props.disabled)) {
    return;
  }
  if (isNonClearable.value) {
    return;
  }
  selectedModel.value = null;
}

function syncOpenFromProp(value) {
  open.value = toBoolean(value);
}

function emitOpenChange(value) {
  emit("update:open", value);
}

watch(() => props.open, syncOpenFromProp);
watch(open, emitOpenChange);

function handleUpdateSearch({ detail }) {
  if (toBoolean(props.disabled)) {
    return;
  }
  const [value] = detail;
  store.searchQuery.value = value;

  const isActive = store.hasSearchQuery.value;
  if (searchWasActive.value !== isActive) {
    // Only remount when drilled into a sub-frame: that is the sole reason to
    // discard the frame stack. At root the store recomputes `models` to flat
    // search results reactively, so remounting there would needlessly tear down
    // the focused search input and drop the caret after the first keystroke.
    if (store.hasParent.value) {
      searchResetKey.value++;
    }
    if (!isActive) {
      store.resetCurrentItem();
    }
  }
  searchWasActive.value = isActive;
}

const picker = useTemplateRef("picker");

// Commits the highlighted plan: the first match within the current carrier. Nothing is picked
// while no row is highlighted: no query yet, no match, or the plan list still loading.
function handleSearchEnter({ detail }) {
  if (toBoolean(props.disabled)) {
    return;
  }
  const [keyboardEvent] = detail;
  if (!isComposing(keyboardEvent)) {
    picker.value?.selectFirstResult?.({ markedOnly: true });
  }
}

function handleUpdateOpen({ detail }) {
  if (toBoolean(props.disabled)) {
    return;
  }
  const [isOpen] = detail;
  open.value = isOpen;
}

async function loadChildren(item) {
  return store.getChildrenOf(item);
}

function handleCurrentItemChange({ detail }) {
  if (toBoolean(props.disabled)) {
    return;
  }
  const [item] = detail;
  store.setCurrentItem(item);
}

const classModifiers = computed(() => ({
  "--inline": props.inline,
  "--permanent": toBoolean(props.permanent),
  "--has-selected-model": !!selectedModel.value,
}));
</script>

<style>
:host {
  --app-slide-animation-time: 0.3s;
  --filter-width: 100%;
  display: block;
  box-sizing: border-box;
}
:host([chrome="bar"]) {
  --filter-width: max-content;
}
</style>

<style lang="scss" scoped>
sp-quantum-select {
  --quantum-select-animation-time: var(--app-slide-animation-time);
  --sp-comp-list-item-prepend-margin-right: 0.5rem;
  height: auto;
}

// In permanent mode (e.g. the required-filters modal) the plan list renders
// inline and must fill the available height. Elsewhere the quantum select is
// just an activator that should size to its content so it aligns vertically
// with the adjacent search controls.
.insurance-plan-filter.--permanent sp-quantum-select {
  height: 100%;
}

.insurance-plan-filter__progress {
  --sp-comp-progress-transition-duration: var(--app-slide-animation-time);

  margin: 0 0.75rem 0.75rem 0.75rem;
}

.insurance-plan-filter {
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 100%;
  &.--inline {
    display: inline-flex;
    // The full-height rule above is only meant for the dialog's scrollable
    // plan list. Inline (the "Change plan" activator) must size to its button,
    // otherwise a stretched grid/flex parent blows the activator up to 100%.
    // It also sits within text flow, so align it to the line and let the inner
    // select hug its content rather than the form-field height, which would
    // otherwise push the box below the surrounding text baseline.
    height: auto;
    vertical-align: middle;
    --filter-width: auto;
    align-self: center;

    sp-quantum-select {
      height: auto;
    }
  }
  &.--has-selected-model {
    .activator {
      --sp-comp-text-field-color: var(--sp-sys-color-on-surface);
    }
  }
}

sp-quantum-select,
.activator,
.insurance-plan-filter {
  --sp-comp-button-width: var(--filter-width);

  width: var(--filter-width);
}
</style>
